import { clear, el } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import { minigameState } from '../../../storage/db.js';
import './blackjack.css';

const MODULE_ID = 'blackjack';
const MIN_BANKROLL = 10;
const STARTING_BANKROLL = 100;
const TOPUP_AMOUNT = 25;
const CHIP_VALUES = [5, 10, 25, 50, 100] as const;
const AUTO_DEAL_MS = 2200;

type Suit = '♠' | '♥' | '♦' | '♣';
type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';
interface Card { rank: Rank; suit: Suit }

interface Persisted {
  bankroll: number;
  handsPlayed: number;
  wins: number;
  losses: number;
  pushes: number;
  blackjacks: number;
  bestBankroll: number;
  currentStreak: number;
  longestStreak: number;
  lastBet: number;
}

const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUITS: Suit[] = ['♠', '♥', '♦', '♣'];

function buildShoe(decks = 6): Card[] {
  const cards: Card[] = [];
  for (let d = 0; d < decks; d++) for (const r of RANKS) for (const s of SUITS) cards.push({ rank: r, suit: s });
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = cards[i]!; const b = cards[j]!;
    cards[i] = b; cards[j] = a;
  }
  return cards;
}

function cardValue(c: Card): number {
  if (c.rank === 'A') return 11;
  if (c.rank === 'J' || c.rank === 'Q' || c.rank === 'K' || c.rank === '10') return 10;
  return Number(c.rank);
}

function handTotal(hand: Card[]): { total: number; soft: boolean } {
  let total = 0; let aces = 0;
  for (const c of hand) { total += cardValue(c); if (c.rank === 'A') aces++; }
  while (total > 21 && aces > 0) { total -= 10; aces--; }
  return { total, soft: aces > 0 && total <= 21 };
}

function isBlackjack(hand: Card[]): boolean {
  return hand.length === 2 && handTotal(hand).total === 21;
}

function defaultState(): Persisted {
  return {
    bankroll: STARTING_BANKROLL,
    handsPlayed: 0, wins: 0, losses: 0, pushes: 0, blackjacks: 0,
    bestBankroll: STARTING_BANKROLL,
    currentStreak: 0, longestStreak: 0,
    lastBet: 10,
  };
}

async function loadPersisted(): Promise<Persisted> {
  const stored = await minigameState.get<Persisted>(MODULE_ID);
  if (!stored) return defaultState();
  return { ...defaultState(), ...stored };
}

async function savePersisted(p: Persisted): Promise<void> {
  await minigameState.put(MODULE_ID, p);
}

interface PlayerHand { cards: Card[]; bet: number; doubled: boolean; settled?: 'win' | 'lose' | 'push' | 'blackjack' }

interface GameState {
  shoe: Card[];
  dealer: Card[];
  hands: PlayerHand[];
  activeHand: number;
  phase: 'betting' | 'player' | 'dealer' | 'settle';
  hideHole: boolean;
  bet: number;
  history: Array<'win' | 'lose' | 'push'>;
}

export const blackjack: RescueModule = {
  id: MODULE_ID,
  name: 'Blackjack',
  kind: 'minigame',
  defaultDurationSec: 180,
  reducedMotion: true,
  safety: {
    gamblingLike: true,
    excludeForDomains: ['gambling'],
    experimental: false,
    reducedMotionSafe: true,
  },
  async run(host) {
    return new Promise<RescueResult>((resolve) => { void mount(host, resolve); });
  },
};

async function mount(host: HTMLElement, resolve: (r: RescueResult) => void) {
  clear(host);
  document.body.classList.add('bj-active');
  const persisted = await loadPersisted();
  if (persisted.bankroll < MIN_BANKROLL) persisted.bankroll = MIN_BANKROLL;

  const game: GameState = {
    shoe: buildShoe(6),
    dealer: [], hands: [], activeHand: 0,
    phase: 'betting', hideHole: true,
    bet: Math.min(Math.max(MIN_BANKROLL, persisted.lastBet), persisted.bankroll),
    history: [],
  };

  const stage = el('div', { class: 'bj-stage' });

  // ---- Topbar ----
  const bankrollEl = el('div', { class: 'bj-bankroll' }, formatMoney(persisted.bankroll));
  const streakEl = el('div', { class: 'bj-streak' }, el('span', { class: 'flame' }, '✦'), el('span', { dataset: { role: 'streak-num' } }, String(persisted.currentStreak)));

  const topbar = el('div', { class: 'bj-topbar' },
    el('div', { class: 'bj-bankroll-wrap' },
      el('div', { class: 'bj-bankroll-label' }, 'Bankroll'),
      bankrollEl,
    ),
    streakEl,
    el('button', { class: 'bj-close-x', attrs: { 'aria-label': 'Close blackjack' }, onclick: () => doClose() }, '×'),
  );

  const miniStats = el('div', { class: 'bj-mini-stats' },
    el('span', {}, 'Hands ', el('b', { dataset: { role: 'hands' } }, String(persisted.handsPlayed))),
    el('span', {}, 'Wins ', el('b', { dataset: { role: 'wins' } }, String(persisted.wins))),
    el('span', {}, 'BJ ', el('b', { dataset: { role: 'bjs' } }, String(persisted.blackjacks))),
    el('span', {}, 'Best ', el('b', { dataset: { role: 'best' } }, formatMoney(persisted.bestBankroll))),
  );

  // ---- Felt ----
  const felt = el('div', { class: 'bj-felt' });
  const dealerRow = el('div', { class: 'bj-row' });
  const dealerLabel = el('div', { class: 'label' }, 'Dealer ', el('span', { class: 'total', dataset: { role: 'dealer-total' } }, '–'));
  const dealerCards = el('div', { class: 'bj-cards', dataset: { role: 'dealer-cards' } });
  dealerRow.appendChild(dealerLabel); dealerRow.appendChild(dealerCards);
  const playerRow = el('div', { class: 'bj-row' });
  const playerLabel = el('div', { class: 'label' }, 'You ', el('span', { class: 'total', dataset: { role: 'player-total' } }, '–'));
  const playerCards = el('div', { class: 'bj-cards', dataset: { role: 'player-cards' } });
  playerRow.appendChild(playerLabel); playerRow.appendChild(playerCards);
  felt.appendChild(dealerRow);
  felt.appendChild(playerRow);

  // ---- Bet row ----
  const betAmount = el('div', { class: 'bj-bet-amount' },
    el('span', { class: 'lbl' }, 'Bet'),
    el('span', { dataset: { role: 'bet-display' } }, formatMoney(game.bet)),
  );
  const chipRow = el('div', { class: 'bj-chip-row' });
  for (const v of CHIP_VALUES) {
    chipRow.appendChild(el('button', { class: 'chip', dataset: { v: String(v) }, onclick: () => addToBet(v) }, `$${v}`));
  }
  const betControls = el('div', { class: 'bj-bet-controls' },
    el('button', { class: 'tag', attrs: { type: 'button' }, onclick: () => { game.bet = MIN_BANKROLL; updateBetDisplay(); refreshActions(); } }, 'Min'),
    el('button', { class: 'tag', attrs: { type: 'button' }, onclick: () => { game.bet = Math.max(MIN_BANKROLL, Math.floor(persisted.bankroll / 2)); updateBetDisplay(); refreshActions(); } }, '½'),
    el('button', { class: 'tag', attrs: { type: 'button' }, onclick: () => { game.bet = persisted.bankroll; updateBetDisplay(); refreshActions(); } }, 'Max'),
  );
  const betRow = el('div', { class: 'bj-bet-row' }, betAmount, chipRow, betControls);

  // ---- Actions ----
  const actionsRow = el('div', { class: 'bj-actions' });
  const dealBtn = el('button', { class: 'btn primary', onclick: () => deal() }, 'Deal');
  const hitBtn = el('button', { class: 'btn', onclick: () => hit() }, 'Hit');
  const standBtn = el('button', { class: 'btn', onclick: () => stand() }, 'Stand');
  const doubleBtn = el('button', { class: 'btn', onclick: () => doubleDown() }, 'Double');
  const splitBtn = el('button', { class: 'btn', onclick: () => splitHand() }, 'Split');
  actionsRow.appendChild(dealBtn);
  actionsRow.appendChild(hitBtn);
  actionsRow.appendChild(standBtn);
  actionsRow.appendChild(doubleBtn);
  actionsRow.appendChild(splitBtn);

  const historyRow = el('div', { class: 'bj-history', dataset: { role: 'history' } });

  stage.appendChild(topbar);
  stage.appendChild(miniStats);
  stage.appendChild(felt);
  stage.appendChild(betRow);
  stage.appendChild(actionsRow);
  stage.appendChild(historyRow);

  host.appendChild(stage);

  // ---- Helpers ----
  function formatMoney(n: number): string { return `$${Math.round(n).toLocaleString()}`; }
  function updateBetDisplay() {
    const node = stage.querySelector('[data-role="bet-display"]') as HTMLElement | null;
    if (node) node.textContent = formatMoney(game.bet);
  }

  let autoDealCard: HTMLElement | null = null;
  let autoDealTimer: number | null = null;
  let autoDealRaf: number | null = null;

  function refreshActions() {
    const inHand = game.phase === 'player';
    const settled = game.phase === 'betting' || game.phase === 'settle';
    stage.classList.toggle('player-turn', inHand);
    dealBtn.disabled = !settled || persisted.bankroll < MIN_BANKROLL || game.bet < 5 || game.bet > persisted.bankroll;
    hitBtn.disabled = !inHand;
    standBtn.disabled = !inHand;
    const hand = game.hands[game.activeHand];
    doubleBtn.disabled = !inHand || !hand || hand.cards.length !== 2 || persisted.bankroll < hand.bet;
    const canSplit = inHand && !!hand && hand.cards.length === 2 && !!hand.cards[0] && !!hand.cards[1] && cardValue(hand.cards[0]) === cardValue(hand.cards[1]) && persisted.bankroll >= hand.bet && game.hands.length === 1;
    splitBtn.disabled = !canSplit;
    splitBtn.style.display = canSplit ? '' : 'none';
    const chipDisabled = !settled;
    for (const c of [...stage.querySelectorAll('.chip[data-v]')]) (c as HTMLButtonElement).disabled = chipDisabled;
    for (const c of [...betControls.children]) (c as HTMLButtonElement).disabled = chipDisabled;

    // Bet display dim when locked
    betAmount.style.opacity = settled ? '1' : '0.55';

    // Hit/stand/etc only relevant during play; show dealBtn full width when betting
    if (settled) {
      hitBtn.style.display = 'none';
      standBtn.style.display = 'none';
      doubleBtn.style.display = 'none';
      dealBtn.style.gridColumn = '1 / -1';
    } else {
      hitBtn.style.display = '';
      standBtn.style.display = '';
      doubleBtn.style.display = '';
      dealBtn.style.gridColumn = '';
      dealBtn.style.display = 'none';
    }
    if (settled && game.phase === 'settle') dealBtn.style.display = '';
    else if (!settled) dealBtn.style.display = 'none';
    else dealBtn.style.display = '';
  }

  function addToBet(v: number) {
    if (game.phase !== 'betting' && game.phase !== 'settle') return;
    cancelAutoDeal();
    const next = game.bet + v;
    game.bet = Math.min(persisted.bankroll, Math.max(MIN_BANKROLL, next));
    updateBetDisplay(); refreshActions();
  }

  function drawCard(): Card {
    if (game.shoe.length < 26) game.shoe = buildShoe(6);
    return game.shoe.shift()!;
  }

  function cardKey(c: Card): string { return `${c.rank}${c.suit}`; }

  function reconcileCards(
    host: HTMLElement,
    keys: string[],
    builder: (k: string, idx: number) => HTMLElement,
    classifyAnim?: (prevAtPos: string | undefined, next: string) => string,
  ): void {
    const existing = [...host.children] as HTMLElement[];
    let i = 0;
    for (; i < keys.length && i < existing.length; i++) {
      if (existing[i]?.dataset['key'] !== keys[i]) break;
    }
    const removedKeys = existing.slice(i).map((n) => n.dataset['key']);
    for (let j = existing.length - 1; j >= i; j--) {
      const node = existing[j];
      if (node) host.removeChild(node);
    }
    for (let j = i; j < keys.length; j++) {
      const k = keys[j];
      if (k === undefined) continue;
      const node = builder(k, j);
      node.dataset['key'] = k;
      const animClass = classifyAnim ? classifyAnim(removedKeys[j - i], k) : 'fresh';
      const newIdx = j - i;
      if (animClass) {
        node.classList.add(animClass);
        node.style.setProperty('--bj-anim-i', String(newIdx));
      }
      host.appendChild(node);
    }
  }

  function applySettledClasses(host: HTMLElement, settled: PlayerHand['settled']): void {
    for (const n of [...host.children] as HTMLElement[]) {
      n.classList.remove('win', 'lose');
      if (settled === 'win' || settled === 'blackjack') n.classList.add('win');
      if (settled === 'lose') n.classList.add('lose');
    }
  }

  function renderCards() {
    // ---- Dealer reconciliation ----
    const dealerKeys = game.dealer.map((c, i) =>
      (i === 1 && game.hideHole) ? 'BACK' : cardKey(c),
    );
    reconcileCards(dealerCards, dealerKeys, (k, idx) => {
      if (k === 'BACK') return el('div', { class: 'card-back' });
      const c = game.dealer[idx];
      return c ? renderCard(c) : el('div', { class: 'card-back' });
    }, (prev, next) => {
      if (prev === 'BACK' && next !== 'BACK') return 'flip';
      return 'fresh';
    });

    const firstDealer = game.dealer[0];
    const dt = handTotal(game.hideHole && game.dealer.length > 1 && firstDealer ? [firstDealer] : game.dealer);
    setText('[data-role="dealer-total"]', `${dt.total}${dt.soft ? '·s' : ''}`);

    // ---- Player wraps reconciliation ----
    const wraps = [...playerCards.children] as HTMLElement[];
    while (wraps.length > game.hands.length) {
      const popped = wraps.pop();
      if (popped) playerCards.removeChild(popped);
    }
    for (let h = 0; h < game.hands.length; h++) {
      const hand = game.hands[h];
      if (!hand) continue;
      let wrap = wraps[h];
      if (!wrap) {
        wrap = el('div', { class: 'bj-hand' });
        playerCards.appendChild(wrap);
      }
      const isActive = h === game.activeHand && game.phase === 'player';
      wrap.classList.toggle('active', isActive);

      let cardsHost = wrap.querySelector('.bj-hand-cards') as HTMLElement | null;
      if (!cardsHost) {
        cardsHost = el('div', { class: 'bj-hand-cards' });
        wrap.appendChild(cardsHost);
      }
      const keys = hand.cards.map((c) => cardKey(c));
      reconcileCards(cardsHost, keys, (_k, idx) => {
        const c = hand.cards[idx];
        return c ? renderCard(c) : el('div', { class: 'card-back' });
      });
      applySettledClasses(cardsHost, hand.settled);

      let tag = wrap.querySelector('.bj-hand-tag') as HTMLElement | null;
      if (game.hands.length > 1) {
        if (!tag) {
          tag = el('span', { class: 'tag bj-hand-tag' });
          wrap.appendChild(tag);
        }
        tag.textContent = `H${h + 1} · ${formatMoney(hand.bet)}`;
      } else if (tag) {
        tag.remove();
      }
    }
    const activeForTotal = game.hands[game.activeHand];
    const pt = activeForTotal ? handTotal(activeForTotal.cards).total : 0;
    setText('[data-role="player-total"]', pt > 0 ? String(pt) : '–');
  }

  function setText(sel: string, value: string) {
    const node = stage.querySelector(sel) as HTMLElement | null;
    if (node) node.textContent = value;
  }
  function renderCard(c: Card): HTMLElement {
    const isRed = c.suit === '♥' || c.suit === '♦';
    return el('div', { class: `card-face${isRed ? ' red' : ''}` },
      el('div', { class: 'corner' }, el('span', {}, c.rank), el('span', {}, c.suit)),
      el('div', { class: 'pip' }, c.suit),
      el('div', { class: 'corner bottom' }, el('span', {}, c.rank), el('span', {}, c.suit)),
    );
  }

  function popMessage(text: string, kind: 'win' | 'lose' | 'push') {
    const wrap = el('div', { class: 'bj-message' });
    wrap.appendChild(el('div', { class: `pop ${kind}` }, text));
    stage.appendChild(wrap);
    setTimeout(() => wrap.remove(), 1300);
    if (kind === 'win') confetti();
  }

  function deltaFloat(amount: number) {
    const sign = amount >= 0 ? '+' : '−';
    const node = el('div', { class: `bj-delta ${amount >= 0 ? 'win' : 'lose'}` }, `${sign}${formatMoney(Math.abs(amount))}`);
    topbar.appendChild(node);
    setTimeout(() => node.remove(), 1500);
  }

  function confetti() {
    const wrap = el('div', { class: 'bj-confetti' });
    const colors = ['#34d399', '#fbbf24', '#7dd3fc', '#a78bfa', '#f472b6', '#f87171'];
    for (let i = 0; i < 28; i++) {
      const s = document.createElement('span');
      s.style.left = `${Math.random() * 100}%`;
      s.style.background = colors[i % colors.length] ?? '#34d399';
      s.style.animationDelay = `${Math.random() * 0.3}s`;
      s.style.transform = `rotate(${Math.random() * 360}deg)`;
      wrap.appendChild(s);
    }
    stage.appendChild(wrap);
    setTimeout(() => wrap.remove(), 1800);
  }

  function flashBankroll(delta: number) {
    bankrollEl.classList.remove('flash', 'lose');
    if (delta > 0) bankrollEl.classList.add('flash');
    if (delta < 0) bankrollEl.classList.add('lose');
    setTimeout(() => bankrollEl.classList.remove('flash', 'lose'), 700);
  }

  function pushHistory(r: 'win' | 'lose' | 'push') {
    game.history.unshift(r);
    if (game.history.length > 16) game.history.pop();
    clear(historyRow);
    for (const h of game.history) {
      historyRow.appendChild(el('span', { class: `hpill ${h}` }, h === 'win' ? 'W' : h === 'lose' ? 'L' : 'P'));
    }
  }

  function syncStats() {
    setText('[data-role="streak-num"]', String(persisted.currentStreak));
    streakEl.classList.toggle('hot', persisted.currentStreak >= 3);
    setText('[data-role="hands"]', String(persisted.handsPlayed));
    setText('[data-role="wins"]', String(persisted.wins));
    setText('[data-role="bjs"]', String(persisted.blackjacks));
    setText('[data-role="best"]', formatMoney(persisted.bestBankroll));
  }

  // ---- Auto-deal ----
  function startAutoDeal() {
    cancelAutoDeal();
    autoDealCard = el('div', { class: 'bj-autodeal' },
      el('div', {},
        el('div', { attrs: { style: 'font-weight:600; font-size:0.92rem;' } }, `Same bet (${formatMoney(game.bet)})`),
        el('div', { attrs: { style: 'font-size:0.75rem; opacity:0.75;' } }, 'tap chip to change'),
      ),
      el('div', { class: 'progress' }, el('span', { dataset: { role: 'auto-bar' } })),
      el('div', { class: 'countdown', dataset: { role: 'auto-count' } }, '2'),
      el('button', { class: 'tag', attrs: { type: 'button' }, onclick: () => cancelAutoDeal() }, 'Hold'),
    );
    stage.insertBefore(autoDealCard, betRow);

    const start = performance.now();
    const bar = autoDealCard.querySelector('[data-role="auto-bar"]') as HTMLElement | null;
    const count = autoDealCard.querySelector('[data-role="auto-count"]') as HTMLElement | null;
    const tick = () => {
      if (!autoDealCard) return;
      const elapsed = performance.now() - start;
      const progress = Math.min(1, elapsed / AUTO_DEAL_MS);
      if (bar) bar.style.transform = `scaleX(${1 - progress})`;
      if (count) count.textContent = String(Math.max(0, Math.ceil((AUTO_DEAL_MS - elapsed) / 1000)));
      if (progress >= 1) return;
      autoDealRaf = requestAnimationFrame(tick);
    };
    autoDealRaf = requestAnimationFrame(tick);
    autoDealTimer = window.setTimeout(() => { cancelAutoDeal(); deal(); }, AUTO_DEAL_MS);
  }
  function cancelAutoDeal() {
    if (autoDealTimer != null) { window.clearTimeout(autoDealTimer); autoDealTimer = null; }
    if (autoDealRaf != null) { cancelAnimationFrame(autoDealRaf); autoDealRaf = null; }
    if (autoDealCard) { autoDealCard.remove(); autoDealCard = null; }
  }

  function doClose() {
    cancelAutoDeal();
    document.body.classList.remove('bj-active');
    void savePersisted({ ...persisted, lastBet: game.bet });
    resolve({ kind: 'completed', details: { handsPlayed: persisted.handsPlayed, finalBankroll: persisted.bankroll } });
  }

  // ---- Round flow ----
  function deal() {
    cancelAutoDeal();
    if (persisted.bankroll < MIN_BANKROLL) persisted.bankroll = MIN_BANKROLL;
    if (game.bet > persisted.bankroll) game.bet = persisted.bankroll;
    if (game.bet < 5) game.bet = 5;

    game.phase = 'player';
    game.hideHole = true;
    game.dealer = [];
    game.hands = [{ cards: [], bet: game.bet, doubled: false }];
    game.activeHand = 0;

    persisted.bankroll -= game.bet;
    bankrollEl.textContent = formatMoney(persisted.bankroll);

    const first = game.hands[0];
    if (!first) return;
    first.cards.push(drawCard());
    game.dealer.push(drawCard());
    first.cards.push(drawCard());
    game.dealer.push(drawCard());
    renderCards();
    refreshActions();

    if (isBlackjack(first.cards)) {
      game.hideHole = false;
      if (isBlackjack(game.dealer)) settleHand(0, 'push');
      else settleHand(0, 'blackjack');
      finishRound();
      return;
    }
    if (isBlackjack(game.dealer)) {
      game.hideHole = false;
      settleHand(0, 'lose');
      finishRound();
      return;
    }
  }

  function hit() {
    const hand = game.hands[game.activeHand];
    if (!hand) return;
    hand.cards.push(drawCard());
    renderCards();
    const total = handTotal(hand.cards).total;
    if (total > 21) { settleHand(game.activeHand, 'lose'); advanceHand(); }
    else if (total === 21) advanceHand();
    refreshActions();
  }

  function stand() { advanceHand(); }

  function doubleDown() {
    const hand = game.hands[game.activeHand];
    if (!hand) return;
    if (hand.cards.length !== 2) return;
    if (persisted.bankroll < hand.bet) return;
    persisted.bankroll -= hand.bet;
    hand.bet *= 2; hand.doubled = true;
    bankrollEl.textContent = formatMoney(persisted.bankroll);
    hand.cards.push(drawCard());
    renderCards();
    if (handTotal(hand.cards).total > 21) settleHand(game.activeHand, 'lose');
    advanceHand();
  }

  function splitHand() {
    const hand = game.hands[0];
    if (!hand) return;
    if (hand.cards.length !== 2) return;
    const c0 = hand.cards[0]; const c1 = hand.cards[1];
    if (!c0 || !c1) return;
    if (cardValue(c0) !== cardValue(c1)) return;
    if (persisted.bankroll < hand.bet) return;
    persisted.bankroll -= hand.bet;
    bankrollEl.textContent = formatMoney(persisted.bankroll);
    const popped = hand.cards.pop();
    if (!popped) return;
    const second: PlayerHand = { cards: [popped], bet: hand.bet, doubled: false };
    hand.cards.push(drawCard());
    second.cards.push(drawCard());
    game.hands.push(second);
    renderCards();
    refreshActions();
  }

  function advanceHand() {
    const current = game.hands[game.activeHand];
    if (game.activeHand < game.hands.length - 1 && current && !current.settled) {
      game.activeHand++;
      renderCards(); refreshActions();
      return;
    }
    if (game.activeHand < game.hands.length - 1) {
      game.activeHand++;
      renderCards(); refreshActions();
      return;
    }
    dealerPlay();
  }

  function dealerPlay() {
    game.phase = 'dealer';
    game.hideHole = false;
    renderCards();
    const liveHand = game.hands.some((h) => !h.settled && handTotal(h.cards).total <= 21);
    if (!liveHand) { finishRound(); return; }
    const stepDealer = () => {
      const t = handTotal(game.dealer);
      if (t.total < 17 || (t.total === 17 && t.soft)) {
        game.dealer.push(drawCard());
        renderCards();
        setTimeout(stepDealer, 360);
      } else {
        for (let i = 0; i < game.hands.length; i++) {
          const h = game.hands[i];
          if (!h || h.settled) continue;
          const ph = handTotal(h.cards).total;
          const dh = handTotal(game.dealer).total;
          if (dh > 21 || ph > dh) settleHand(i, 'win');
          else if (ph === dh) settleHand(i, 'push');
          else settleHand(i, 'lose');
        }
        finishRound();
      }
    };
    setTimeout(stepDealer, 420);
  }

  function settleHand(idx: number, outcome: 'win' | 'lose' | 'push' | 'blackjack') {
    const hand = game.hands[idx];
    if (!hand) return;
    hand.settled = outcome;
    let payout = 0;
    if (outcome === 'win') payout = hand.bet * 2;
    else if (outcome === 'blackjack') payout = Math.round(hand.bet * 2.5);
    else if (outcome === 'push') payout = hand.bet;
    if (payout > 0) persisted.bankroll += payout;
  }

  function finishRound() {
    game.phase = 'settle';
    renderCards();

    let netDelta = 0;
    let anyWin = false; let anyPush = false; let anyBJ = false;
    for (const h of game.hands) {
      const stake = h.bet;
      if (h.settled === 'win') { netDelta += stake; anyWin = true; }
      else if (h.settled === 'blackjack') { netDelta += Math.round(stake * 1.5); anyWin = true; anyBJ = true; }
      else if (h.settled === 'push') { anyPush = true; }
      else if (h.settled === 'lose') { netDelta -= stake; }
    }

    persisted.handsPlayed += game.hands.length;
    if (anyWin) persisted.wins += 1;
    else if (anyPush) persisted.pushes += 1;
    else persisted.losses += 1;
    if (anyBJ) persisted.blackjacks += 1;

    if (anyWin) {
      persisted.currentStreak += 1;
      persisted.longestStreak = Math.max(persisted.longestStreak, persisted.currentStreak);
    } else if (!anyPush) {
      persisted.currentStreak = 0;
    }

    persisted.bestBankroll = Math.max(persisted.bestBankroll, persisted.bankroll);

    let topUp = 0;
    if (persisted.bankroll < MIN_BANKROLL) {
      topUp = Math.max(MIN_BANKROLL - persisted.bankroll, TOPUP_AMOUNT);
      persisted.bankroll += topUp;
    }

    bankrollEl.textContent = formatMoney(persisted.bankroll);
    syncStats();
    flashBankroll(netDelta);
    if (netDelta !== 0) deltaFloat(netDelta);

    if (anyWin) {
      const message = anyBJ
        ? `Blackjack! +${formatMoney(netDelta)}`
        : persisted.currentStreak >= 3
          ? `${persisted.currentStreak}× streak! +${formatMoney(netDelta)}`
          : `Win +${formatMoney(netDelta)}`;
      popMessage(message, 'win');
      pushHistory('win');
    } else if (anyPush) {
      popMessage('Push', 'push');
      pushHistory('push');
    } else {
      popMessage(`−${formatMoney(Math.abs(netDelta))}`, 'lose');
      pushHistory('lose');
    }
    if (topUp > 0) {
      setTimeout(() => popMessage(`Top-up +${formatMoney(topUp)}`, 'push'), 900);
    }

    persisted.lastBet = game.bet;
    void savePersisted(persisted);

    game.phase = 'settle';
    game.bet = Math.min(Math.max(MIN_BANKROLL, game.bet), persisted.bankroll);
    updateBetDisplay();
    refreshActions();

    // Auto-deal countdown for flow
    startAutoDeal();
  }

  // Initial paint
  renderCards();
  syncStats();
  refreshActions();
}
